/*
 * Die Anwendertexte des Abschnitts „Dateien“ am Vorgang und seiner zwei Panels (Spec „Oberfläche › Abschnitt ‚Dateien‘“,
 * Festlegung 16; Hausregel „Texte in Properties“): Schlüssel englisch, Werte deutsch. Die Lösch- und Lösensätze gelten
 * auch im Kundenreiter (F3, Aufgabe 25).
 */

/** Trenner der Ordnerebenen in einem Pfad. */
const PATH_SEP = " › ";
const path = (parts: readonly string[]): string => parts.join(PATH_SEP);

/**
 * Die Grenzen des Hochladens, die das Web VOR dem Senden prüft. `maxFilesPerUpload` spiegelt `UPLOAD_MAX_FILES` der
 * Domäne (K1) — web importiert domain nie (check-boundaries); ein Test hält beide gleich. `maxBytes` ist die Grenze der
 * Kundenablage (50 MB, `FILE_MAX_BYTES` der API).
 */
export const DOCUMENT_FILE_LIMITS = {
  maxFilesPerUpload: 20,
  maxBytes: 50 * 1024 * 1024,
} as const;

export const DOCUMENT_FILE_TEXT = {
  // --- Tabelle ---
  columnFile: "Datei",
  columnOrigin: "Herkunft",
  columnFolder: "Ordner",
  columnCompanies: "Unternehmen",
  columnLinked: "Verknüpft",
  columnActions: "Aktionen",
  empty: "Mit diesem Vorgang sind noch keine Dateien verknüpft.",
  loading: "Lädt …",
  loadFailed: "Die Dateien konnten nicht geladen werden.",
  /** Ein Ordnerpfad, etwa „Angebote › 2026 › ANG-2026-0003“. */
  path,
  /** Die Wurzel der Kundenablage (Datei ohne Ordner, Baum ohne Wahl) — wie im Kundenreiter. */
  rootFolder: "Alle Dateien",
  /** Zugänglicher Name des Dateinamens (Link zum Herunterladen). */
  download: (name: string): string => `${name} herunterladen`,
  downloadFailed: (name: string): string => `${name} konnte nicht heruntergeladen werden.`,
  /** Zugänglicher Name des Ordnersprungs in den Kundenreiter. */
  openFolder: (parts: readonly string[]): string => `Ordner ${path(parts)} im Reiter Dateien öffnen`,
  /** „Verknüpft“: Datum und Person. */
  linkedBy: (date: string, person: string | null): string => (person === null ? date : `${date} · ${person}`),

  // --- Herkunft (Kennzeichen ohne Rahmen) ---
  /** Beleg-PDF, am Angebot mit Versionszusatz — dieselbe Regel wie am Angebot: leer bei 1, sonst „v‹n›“. */
  documentPdf: (v: number | null): string => (v !== null && v >= 2 ? `Beleg-PDF v${String(v)}` : "Beleg-PDF"),
  fromOffer: (nr: string): string => `aus Angebot ${nr}`,

  // --- Lesend (ältere Angebotsversion; Beleg eines Lieferantenvorgangs, Entscheid 518) ---
  readOnly: "nur lesend",
  readOnlyOlder: "Dateien ändern geht an der jüngsten Version",
  /** Der Grund am Kennzeichen „nur lesend“ einer Datei an Liefervertrag oder Eingangsrechnung (Sofort-Popup). */
  readOnlySupplier: "Beleg eines Liefervertrags oder einer Eingangsrechnung — Ändern nur mit Lieferantensicht",

  // --- Werkzeugleiste ---
  upload: "Dateien hochladen",
  linkExisting: "Vorhandene Datei verknüpfen",

  // --- Hochladen ---
  filesField: "Dateien",
  /** „Ablage: Angebote › 2026 › ANG-2026-0003“ */
  storage: (parts: readonly string[]): string => `Ablage: ${path(parts)}`,
  willCreate: "wird beim Hochladen angelegt",
  otherPlace: "Anderen Ort wählen",
  defaultPlace: "Vorgabe nehmen",
  choosePlaceTitle: "Ablageort wählen",
  choosePlaceConfirm: "Übernehmen",
  noFolders: "Noch keine Ordner angelegt.",
  foldersLoadFailed: "Die Ordner konnten nicht geladen werden.",
  category: "Kategorie",
  noCategory: "(ohne)",
  uploadConfirm: "Hochladen",
  done: "Fertig",
  results: "Ergebnis je Datei",
  /** Die gewählten Dateien vor dem Hochladen (Größe bzw. „zu groß“). */
  chosenFiles: "Gewählte Dateien",
  created: "abgelegt und verknüpft",
  tooLarge: "zu groß — höchstens 50 MB je Datei",
  duplicate: (existing: string): string => `liegt schon in der Ablage des Kunden: ${existing}`,
  linkedExisting: "vorhandene Datei verknüpft",
  aborted: "Hochladen abgebrochen",
  /** Sperrkollision beim Ablegen (TICKET_LOCK_RETRY, 1213/1205): die Datei ist nicht abgelegt. */
  lockRetry: "gerade gesperrt — bitte diese Datei erneut hochladen",
  rejected: "nicht abgelegt",
  /** Mehr Dateien in einem Aufruf, als der Server nimmt (ZU_VIELE_DATEIEN): Diese Datei ist nicht abgelegt. */
  tooManyFiles: "zu viele Dateien in einem Schritt — bitte erneut hochladen",
  /** Das Paket dieser Datei (oder ein früheres) scheiterte: Sie ging nicht hinaus. */
  notSent: "nicht gesendet — bitte erneut hochladen",
  uploadFailed: "Das Hochladen ist gescheitert.",
  linkFailed: "Die Datei konnte nicht verknüpft werden.",

  // --- Verknüpfen ---
  search: "Dateien durchsuchen",
  selection: "Dateien zum Verknüpfen",
  alreadyLinked: (name: string): string => `${name} (schon verknüpft)`,
  /** Eine Belegdatei eines Lieferantenvorgangs ohne Lieferantensicht (Entscheid 518): nicht zu verknüpfen. */
  readOnlyFile: (name: string): string => `${name} (nur lesend)`,
  noMatch: "Keine passende Datei.",
  linkConfirm: "Verknüpfen",

  // --- Lösen und Löschen ---
  unlinkAction: "Verknüpfung lösen",
  unlinkActionName: (name: string): string => `Verknüpfung von ${name} lösen`,
  unlinkTitle: "Verknüpfung lösen?",
  unlinkText: (name: string, documentName: string): string =>
    `${name} bleibt in der Ablage des Kunden und ist danach nicht mehr mit ${documentName} verknüpft.`,
  unlinkConfirm: "Lösen",
  unlinkFailed: "Die Verknüpfung konnte nicht gelöst werden.",
  deleteActionName: (name: string): string => `${name} löschen`,
  deleteTitle: "Datei löschen?",
  /** Die Löschbestätigung (470, F3): sichtbare Vorgänge namentlich, verborgene Verknüpfungen als Zahl. Neutral
   *  „Verknüpfungen“, nicht „Vorgänge“: Seit B-K4 zählen auch Ticketbezüge mit. */
  deleteText: (name: string, visible: readonly string[], hidden: number): string => {
    const links = hidden === 1 ? "einer Verknüpfung" : `${String(hidden)} Verknüpfungen`;
    const further = hidden === 1 ? "einer weiteren Verknüpfung" : `${String(hidden)} weiteren Verknüpfungen`;
    // Ohne sichtbare Vorgänge gibt es keine Liste: „… und von 2 Verknüpfungen gelöst, die Sie nicht sehen.“
    const unlinked = visible.length === 0
      ? (hidden === 0 ? "" : ` und von ${links} gelöst, die Sie nicht sehen`)
      : ` und von allen Vorgängen gelöst: ${visible.join(", ")}`
        + (hidden === 0 ? "" : ` und von ${further}, die Sie nicht sehen`);
    return `${name} wird aus der Ablage des Kunden gelöscht${unlinked}. Das lässt sich nicht rückgängig machen.`;
  },
  deleteConfirm: "Löschen",
  deleteFailed: "Die Datei konnte nicht gelöscht werden.",
  // --- Kundenreiter „Dateien“ (Aufgabe 25) ---
  /** Kundenreiter (F3): Verknüpfungen an Vorgängen, die der Betrachter nicht sieht. */
  hiddenLinks: (n: number): string =>
    (n === 1 ? "1 weitere Verknüpfung (nicht sichtbar)" : `${String(n)} weitere Verknüpfungen (nicht sichtbar)`),
} as const;
